import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  enqueueAction,
  getQueuedActions,
  type OfflineAction,
  type QueueActionInput,
  removeAction,
  replaceAction,
} from '../utils/offlineQueue';
import {
  clientAPI,
  employeeAPI,
  serviceAPI,
  productAPI,
  financialAPI,
  notificationAPI,
} from '../services/api';

type ApiName = QueueActionInput['api'];

interface OfflineContextValue {
  isOnline: boolean;
  pendingActions: OfflineAction[];
  lastSyncAt: number | null;
  queueAction: (action: QueueActionInput) => Promise<OfflineAction>;
  processQueue: () => Promise<void>;
}

const defaultOfflineContext: OfflineContextValue = {
  isOnline: true,
  pendingActions: [],
  lastSyncAt: null,
  queueAction: async (action: QueueActionInput) => ({
    id: `offline-${Date.now()}`,
    createdAt: Date.now(),
    retries: 0,
    ...action,
  }),
  processQueue: async () => undefined,
};

const OfflineContext = createContext<OfflineContextValue>(defaultOfflineContext);

const apiMap: Record<ApiName, typeof clientAPI> = {
  client: clientAPI,
  employee: employeeAPI,
  service: serviceAPI,
  product: productAPI,
  financial: financialAPI,
  notification: notificationAPI,
};

const defaultOnline = typeof window !== 'undefined' ? navigator.onLine : true;

export const OfflineProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(defaultOnline);
  const [pendingActions, setPendingActions] = useState<OfflineAction[]>(() => getQueuedActions());
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const refreshQueue = useCallback(() => {
    setPendingActions(getQueuedActions());
  }, []);

  const queueAction = useCallback(
    async (action: QueueActionInput) => {
      const stored = enqueueAction(action);
      refreshQueue();
      return stored;
    },
    [refreshQueue]
  );

  const executeAction = useCallback(async (action: OfflineAction) => {
    const apiInstance = apiMap[action.api] || clientAPI;
    await apiInstance.request({
      url: action.url,
      method: action.method,
      data: action.payload,
      headers: {
        ...(action.method !== 'get' ? { 'X-Offline-Retry': 'true' } : {}),
      },
    });
  }, []);

  const processQueue = useCallback(async () => {
    if (isProcessing) return;
    if (!isOnline) return;
    const actions = getQueuedActions();
    if (actions.length === 0) {
      setLastSyncAt(Date.now());
      return;
    }
    setIsProcessing(true);
    for (const action of actions) {
      try {
        await executeAction(action);
        removeAction(action.id);
      } catch (error) {
        console.warn('[OfflineQueue] Falha ao sincronizar ação', action.id, error);
        const updated = { ...action, retries: action.retries + 1 };
        replaceAction(updated);
      }
    }
    setIsProcessing(false);
    setLastSyncAt(Date.now());
    refreshQueue();
  }, [executeAction, isOnline, isProcessing, refreshQueue]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };
    const handleOffline = () => {
      setIsOnline(false);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (isOnline && pendingActions.length > 0) {
      processQueue();
    }
  }, [isOnline, pendingActions.length, processQueue]);

  const value = useMemo<OfflineContextValue>(
    () => ({
      isOnline,
      pendingActions,
      lastSyncAt,
      queueAction,
      processQueue,
    }),
    [isOnline, pendingActions, lastSyncAt, queueAction, processQueue]
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};

export const useOffline = () => {
  return useContext(OfflineContext);
};

